import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function ParentInvitePage() {
  const { token } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Приглашение не найдено.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/auth/parent-invitations/${token}/`);
        if (!cancelled) setInfo(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'Приглашение недействительно или истекло.');
          setInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleAccept() {
    if (!token) return;
    setAccepting(true);
    setError('');
    try {
      const res = await api.post(`/auth/parent-invitations/${token}/accept/`);
      if (res.data?.user) {
        await refreshUser();
      }
      const studentId = res.data?.student_id || info?.student_id;
      navigate(
        studentId
          ? `/parent/children/${studentId}/questionnaire?from=invite`
          : '/parent',
      );
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось принять приглашение.');
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка приглашения...</p>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="invite-page">
        <h1>Приглашение родителя</h1>
        <div className="alert alert-error">{error}</div>
        <p className="text-secondary">
          Вы можете вернуться на <Link to="/">главную страницу</Link>.
        </p>
      </div>
    );
  }

  const childName = info?.student_display_name || info?.student_username || 'ученика';

  return (
    <div className="invite-page">
      <div className="page-header">
        <h1>Подключение родителя</h1>
        <p className="page-subtitle">
          Вас приглашают как родителя ученика <strong>{childName}</strong>
          {info?.student_username ? ` (@${info.student_username})` : ''}.
        </p>
      </div>

      <div className="card">
        {error && <div className="alert alert-error">{error}</div>}

        {user ? (
          <>
            <p className="text-secondary">
              Вы вошли как <strong>{user.username}</strong>. Нажмите кнопку, чтобы привязать
              аккаунт к ученику и перейти к короткой анкете родителя.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAccept}
              disabled={accepting}
            >
              {accepting ? 'Подключаем...' : 'Подключиться как родитель'}
            </button>
          </>
        ) : (
          <>
            <p className="text-secondary">
              Чтобы подключиться, войдите или зарегистрируйтесь. После регистрации вы сразу
              станете родителем этого ученика (не учеником).
            </p>
            <div className="invite-actions">
              <Link
                to={`/login?next=${encodeURIComponent(`/parent-invite/${token}`)}`}
                className="btn btn-outline"
              >
                Войти
              </Link>
              <Link
                to={`/register?parent=${encodeURIComponent(token)}`}
                className="btn btn-primary"
              >
                Зарегистрироваться
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
